import { useState, useEffect, useRef, useCallback } from "react";
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from "@dnd-kit/core";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useRealtimeMessages } from "../hooks/useRealtimeMessages";
import { usePushNotifications } from "../hooks/usePushNotifications";
import UpgradeBanner from "./UpgradeBanner";
import PricingModal from "./PricingModal";
const STAGES = ["Downloaded", "Screening", "Interview", "Offered", "Hired"];
const REJECTED = "Rejected";
const API_URL = import.meta.env.VITE_API_URL || "https://api.careersorter.co";
const DEGREE_OPTIONS = ["Any", "Bachelor's", "Master's", "PhD"];
const YEAR_OPTIONS = ["Any", 2025, 2026, 2027, 2028];
const LANGUAGE_OPTIONS = ["English", "Finnish", "Swedish", "German", "French", "Spanish", "Chinese", "Russian"];
// Messages are now loaded from the API — no hardcoded initial data
const COLORS = {
  bg: "#F8F7F4",
  surface: "#FFFFFF",
  surfaceAlt: "#F1F0EC",
  border: "#E2E0DA",
  borderLight: "#ECEAE4",
  text: "#1A1917",
  textSecondary: "#6B6860",
  textMuted: "#9C9889",
  accent: "#2B5CE6",
  accentLight: "#EBF0FD",
  accentHover: "#1E4AD4",
  green: "#1A8754",
  greenLight: "#E8F5EF",
  orange: "#C4720A",
  orangeLight: "#FFF4E5",
  red: "#C53030",
  redLight: "#FEE7E7",
  purple: "#6B46C1",
  purpleLight: "#F0EBFA",
};
const stageBadge = (stage) => {
  const map = {
    Downloaded: { bg: COLORS.surfaceAlt, color: COLORS.textSecondary },
    Screening: { bg: COLORS.orangeLight, color: COLORS.orange },
    Interview: { bg: COLORS.accentLight, color: COLORS.accent },
    Offered: { bg: COLORS.purpleLight, color: COLORS.purple },
    Hired: { bg: COLORS.greenLight, color: COLORS.green },
    Rejected: { bg: COLORS.redLight, color: COLORS.red },
  };
  return map[stage] || map.Downloaded;
};
const Badge = ({ children, bg, color, small }) => (
  <span style={{
    display: "inline-block", padding: small ? "2px 8px" : "3px 10px",
    borderRadius: 4, fontSize: small ? 11 : 12, fontWeight: 600,
    backgroundColor: bg, color, letterSpacing: "0.01em", whiteSpace: "nowrap",
  }}>{children}</span>
);
const Button = ({ children, onClick, variant = "default", small, style: extraStyle }) => {
  const base = {
    padding: small ? "5px 10px" : "8px 16px", borderRadius: 6, fontSize: small ? 12 : 13,
    fontWeight: 600, cursor: "pointer", border: "none", transition: "all 0.15s",
    fontFamily: "inherit", lineHeight: 1.4,
  };
  const variants = {
    default: { backgroundColor: COLORS.surfaceAlt, color: COLORS.text, border: `1px solid ${COLORS.border}` },
    primary: { backgroundColor: COLORS.accent, color: "#fff" },
    danger: { backgroundColor: COLORS.redLight, color: COLORS.red },
    ghost: { backgroundColor: "transparent", color: COLORS.textSecondary, padding: small ? "5px 8px" : "8px 12px" },
  };
  return <button onClick={onClick} style={{ ...base, ...variants[variant], ...extraStyle }}>{children}</button>;
};
const Card = ({ children, style }) => (
  <div style={{
    backgroundColor: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`,
    padding: 20, ...style,
  }}>{children}</div>
);
const SectionTitle = ({ children, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <h2 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0, letterSpacing: "-0.01em" }}>{children}</h2>
    {sub && <p style={{ fontSize: 12, color: COLORS.textMuted, margin: "4px 0 0" }}>{sub}</p>}
  </div>
);
const typingDotStyle = (delay) => ({
  width: 6, height: 6, borderRadius: "50%",
  backgroundColor: COLORS.textMuted,
  animation: "typingBounce 1.4s infinite",
  animationDelay: `${delay * 0.2}s`,
});
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const ALLOWED_FILE_TYPES = [...IMAGE_TYPES, "application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/msword"];
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};
const COMPETENCY_LABELS = {
  leadership: "Leadership",
  communication: "Communication",
  analytical: "Analytical",
  teamwork: "Teamwork",
  adaptability: "Adaptability",
};
const CompetencyBar = ({ label, score }) => {
  const pct = (score / 5) * 100;
  const color = score >= 4 ? COLORS.green : score >= 3 ? COLORS.orange : COLORS.red;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{score}/5</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, backgroundColor: COLORS.surfaceAlt }}>
        <div style={{ height: 4, borderRadius: 2, backgroundColor: color, width: `${pct}%`, transition: "width 0.3s ease" }} />
      </div>
    </div>
  );
};
const DraggableKanbanCard = ({ candidate, ...props }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${candidate.id}`,
    data: { candidate },
  });
  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.3 : 1 }}>
      <KanbanCard candidate={candidate} dragListeners={listeners} dragAttributes={attributes} {...props} />
    </div>
  );
};
const DroppableColumn = ({ stage, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `stage-${stage}` });
  return (
    <div ref={setNodeRef} style={{
      backgroundColor: isOver ? COLORS.accentLight : COLORS.surfaceAlt,
      borderRadius: 10, padding: 12, minHeight: 300,
      transition: "background-color 0.2s",
      outline: isOver ? `2px dashed ${COLORS.accent}` : "none",
    }}>
      {children}
    </div>
  );
};
const KanbanCard = ({ candidate, onMove, onReject, onChat, onReport, onRequestCV, onDownloadCV, unreadCount, dragListeners, dragAttributes }) => {
  const [expanded, setExpanded] = useState(false);
  const badge = stageBadge(candidate.stage);
  return (
    <div style={{
      backgroundColor: COLORS.surface, borderRadius: 8, border: `1px solid ${COLORS.borderLight}`,
      padding: 14, marginBottom: 10, transition: "box-shadow 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flex: 1 }}>
          {dragListeners && (
            <span {...dragListeners} {...dragAttributes} style={{
              cursor: "grab", color: COLORS.textMuted, fontSize: 16, lineHeight: "20px",
              userSelect: "none", flexShrink: 0, padding: "2px 4px", borderRadius: 3,
              display: "inline-flex", alignItems: "center",
            }} title="Drag to move">≡</span>
          )}
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{candidate.name}</div>
            <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{candidate.programme}</div>
          </div>
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: "50%", backgroundColor: COLORS.accentLight,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, color: COLORS.accent,
        }}>{candidate.competencyMatch}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Badge bg={COLORS.surfaceAlt} color={COLORS.textSecondary} small>{candidate.school}</Badge>
        {candidate.reportSent && <Badge bg={COLORS.greenLight} color={COLORS.green} small>✓ Reported</Badge>}
      </div>
      {/* Executive Competence Summary toggle — only show if data exists */}
      {candidate.competencies && Object.keys(candidate.competencies).length > 0 && (
        <button onClick={() => setExpanded(!expanded)} style={{
          width: "100%", padding: "6px 0", border: "none", background: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600,
          color: COLORS.accent, fontFamily: "inherit", marginBottom: expanded ? 0 : 6,
        }}>
          <span style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s", display: "inline-block" }}>▸</span>
          Executive Competence Summary
        </button>
      )}
      {expanded && candidate.competencies && (
        <div style={{
          padding: "10px 0 8px", marginBottom: 6,
          borderTop: `1px solid ${COLORS.borderLight}`,
        }}>
          {Object.entries(candidate.competencies).map(([key, val]) => (
            <CompetencyBar key={key} label={COMPETENCY_LABELS[key] || key} score={val} />
          ))}
          <div style={{
            marginTop: 8, padding: "6px 8px", borderRadius: 4,
            backgroundColor: COLORS.surfaceAlt, fontSize: 11, color: COLORS.textSecondary, lineHeight: 1.5,
          }}>
            <span style={{ fontWeight: 600 }}>Summary: </span>
            {Object.entries(candidate.competencies)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 2)
              .map(([k]) => COMPETENCY_LABELS[k])
              .join(" & ")
            } are standout strengths.
            {Object.entries(candidate.competencies)
              .sort((a, b) => a[1] - b[1])
              .slice(0, 1)
              .map(([k, v]) => v <= 3 ? ` ${COMPETENCY_LABELS[k]} could benefit from development.` : "")
            }
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {candidate.cvDownloaded && (
          <div style={{ display: "flex", gap: 4 }}>
            <Button small variant="primary" onClick={() => onDownloadCV(candidate.id, "docx")}>&#11015; DOCX</Button>
            <Button small onClick={() => onDownloadCV(candidate.id, "pdf")}>&#11015; PDF</Button>
          </div>
        )}
        {!candidate.cvDownloaded && !candidate.cvRequested && (
          <Button small variant="primary" onClick={() => onRequestCV(candidate.id)}>📩 Request CV</Button>
        )}
        {!candidate.cvDownloaded && candidate.cvRequested && (
          <Badge bg={COLORS.orangeLight} color={COLORS.orange} small>⏳ CV Requested</Badge>
        )}
        {candidate.stage === "Downloaded" && !candidate.cvDownloaded ? (
          <span title={candidate.cvRequested ? "Waiting for student approval" : "Request CV first"} style={{ cursor: "not-allowed" }}>
            <Button small variant="ghost" style={{ opacity: 0.35, pointerEvents: "none" }}>💬</Button>
          </span>
        ) : (
          <span style={{ position: "relative", display: "inline-block" }}>
            <Button small variant="ghost" onClick={() => onChat(candidate.id)}>💬</Button>
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: -6, right: -6, minWidth: 16, height: 16,
                borderRadius: 8, backgroundColor: COLORS.red, color: "#fff",
                fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center",
                justifyContent: "center", padding: "0 4px",
              }}>{unreadCount}</span>
            )}
          </span>
        )}
        {!candidate.reportSent && (
          <Button small variant="ghost" onClick={() => onReport(candidate.id)}>📤 Report</Button>
        )}
        {candidate.stage !== REJECTED && candidate.stage !== "Hired" && (
          <Button small variant="danger" onClick={() => onReject(candidate.id)}>✕</Button>
        )}
      </div>
    </div>
  );
};
export default function RecruiterDashboard() {
  const { session, profile, signOut, user } = useAuth();
  const authToken = session?.access_token || "";
  const pushNotifications = usePushNotifications(authToken);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeView, setActiveView] = useState("search");
  const [chatOpen, setChatOpen] = useState(null);
  const [msgDraft, setMsgDraft] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const messagesEndRef = useRef(null);
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [msgSearchResults, setMsgSearchResults] = useState([]);
  const [msgSearching, setMsgSearching] = useState(false);
  const [reportLog, setReportLog] = useState([]);
  const [cvRequests, setCvRequests] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [draggedCandidate, setDraggedCandidate] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  // Rejection dialog state
  const [rejectDialog, setRejectDialog] = useState(null); // { id, name, stage }
  const [rejectMessage, setRejectMessage] = useState("");
  const [rejectSending, setRejectSending] = useState(false);
  const EARLY_STAGES = ["Downloaded", "Screening"];
  const DEFAULT_REJECTION_MSG = "Thank you for your interest and for taking the time to apply. After careful consideration, we've decided to move forward with other candidates whose profiles more closely match our current needs. We truly appreciate your effort and wish you the very best in your career journey.";
  // Search state
  const [jdText, setJdText] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [degreeFilter, setDegreeFilter] = useState("Any");
  const [graduationYear, setGraduationYear] = useState("Any");
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [skillsFilter, setSkillsFilter] = useState("");
  const [expandedSearchId, setExpandedSearchId] = useState(null);
  const [requestingCV, setRequestingCV] = useState(new Set());

  // Saved/persistent search state
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchesLoading, setSavedSearchesLoading] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [lastSearchSaved, setLastSearchSaved] = useState(false);

  // --- Unread badges ---
  const [unreadCounts, setUnreadCounts] = useState({});

  // --- Browser notifications ---
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const documentFocusedRef = useRef(true);

  // --- Typing indicators ---
  const [isStudentTyping, setIsStudentTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // --- File attachments ---
  const [attachFile, setAttachFile] = useState(null);
  const [attachPreview, setAttachPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // --- Usage tracking ---
  const [usageBalances, setUsageBalances] = useState(profile?.usage || null);
  const [limitReachedType, setLimitReachedType] = useState(null); // "cv_request" | null
  const [showPricing, setShowPricing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Detect return from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      setPaymentSuccess(true);
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setPaymentSuccess(false), 8000);
    }
    // Handle URL params from notification clicks (when app opens fresh)
    const view = params.get("view");
    if (view) setActiveView(view);
    const chatParam = params.get("chat");
    if (chatParam) setChatOpen(chatParam);
  }, []);

  // Listen for service worker messages (notification clicks while app is open)
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK") {
        const { notificationType, candidateId } = event.data;
        if (notificationType === "new_message" && candidateId) {
          setActiveView("kanban");
          setChatOpen(candidateId);
        } else if (notificationType === "cv_response") {
          setActiveView("incoming");
        } else if (notificationType === "saved_search_match") {
          setActiveView("saved");
        }
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, []);

  // Show push notification prompt after first load (if not yet subscribed)
  useEffect(() => {
    if (pushNotifications.status === "default" || pushNotifications.status === "granted") {
      const dismissed = sessionStorage.getItem("push-prompt-dismissed");
      if (!dismissed) {
        const timer = setTimeout(() => setShowPushPrompt(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [pushNotifications.status]);

  // Load recruiter's CV requests + report log on mount
  useEffect(() => {
    if (!authToken) return;
    loadPipeline();
    loadReports();
  }, [authToken]);

  const loadPipeline = async () => {
    try {
      const data = await apiGet("/recruiter/cv-requests", authToken);
      if (data.success) {
        setCvRequests(data.requests || []);
        // Approved requests become pipeline candidates
        const pipelineCandidates = (data.requests || [])
          .filter(r => r.status === "approved")
          .map(r => ({
            id: r.candidate.id,
            cvRequestId: r.id,
            name: r.candidate.name || "Unknown",
            school: "",
            programme: "",
            downloaded: r.responded_at?.split("T")[0] || r.requested_at?.split("T")[0] || "",
            stage: r.pipeline_stage || "Downloaded",
            competencyMatch: r.match_score ? Math.round(r.match_score * 100) : 0,
            cvDownloaded: true,
            reportSent: r.report_sent || false,
            skills: r.candidate.skills || [],
            cvSummary: r.candidate.cv_summary || "",
          }));
        setCandidates(pipelineCandidates);
      }
    } catch (err) {
      console.error("Failed to load pipeline:", err);
    } finally {
      setPipelineLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const data = await apiGet("/recruiter/reports", authToken);
      if (data.success) {
        setReportLog(data.reports || []);
      }
    } catch (err) {
      console.error("Failed to load reports:", err);
    }
  };

  // --- Saved/persistent searches ---
  const loadSavedSearches = async () => {
    if (!authToken) return;
    setSavedSearchesLoading(true);
    try {
      const data = await apiGet("/searches/saved", authToken);
      setSavedSearches(data.searches || []);
    } catch (err) {
      console.error("Failed to load saved searches:", err);
    } finally {
      setSavedSearchesLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) loadSavedSearches();
  }, [authToken]);

  const handleSaveSearch = async () => {
    if (!jdText.trim()) return;
    setSavingSearch(true);
    setLastSearchSaved(false);
    try {
      const payload = {
        query_text: jdText,
        school_id: profile?.school_id,
        filters: {
          skills: skillsFilter ? skillsFilter.split(",").map(s => s.trim()).filter(Boolean) : [],
          languages: selectedLanguages,
          degree_level: degreeFilter !== "Any" ? degreeFilter : null,
          graduation_year: graduationYear !== "Any" ? graduationYear : null,
        },
      };
      const data = await apiPost("/searches/save", payload, authToken);
      if (data.success) {
        setLastSearchSaved(true);
        loadSavedSearches();
        setTimeout(() => setLastSearchSaved(false), 4000);
      }
    } catch (err) {
      if (err.status === 402) {
        setShowPricing(true);
      } else {
        console.error("Failed to save search:", err);
      }
    } finally {
      setSavingSearch(false);
    }
  };

  const handleToggleSavedSearch = async (searchId, currentlyActive) => {
    try {
      await apiPatch(`/searches/saved/${searchId}`, { active: !currentlyActive }, authToken);
      loadSavedSearches();
    } catch (err) {
      if (err.status === 402) {
        setShowPricing(true);
      } else {
        console.error("Failed to toggle saved search:", err);
      }
    }
  };

  const handleDeleteSavedSearch = async (searchId) => {
    try {
      await apiDelete(`/searches/saved/${searchId}`, authToken);
      loadSavedSearches();
    } catch (err) {
      console.error("Failed to delete saved search:", err);
    }
  };

  // --- Unread counts: fetch + poll every 30s ---
  const fetchUnreadCounts = useCallback(async () => {
    if (!authToken) return;
    try {
      const data = await apiGet("/messages/unread-counts", authToken);
      if (data.success && data.unread_counts) {
        setUnreadCounts(data.unread_counts);
      }
    } catch {
      // Silently degrade if endpoint not available
    }
  }, [authToken]);

  const markMessagesRead = useCallback(async (candidateId) => {
    if (!authToken) return;
    try {
      await apiPost("/messages/mark-read", { candidate_id: String(candidateId) }, authToken);
      setUnreadCounts(prev => {
        const next = { ...prev };
        delete next[String(candidateId)];
        return next;
      });
    } catch {
      // Silently degrade
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) return;
    fetchUnreadCounts();
    // Realtime handles subsequent unread count updates
  }, [authToken, fetchUnreadCounts]);

  // --- Browser notification permission + focus tracking ---
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().then(perm => setNotifPermission(perm));
    }
  }, []);

  useEffect(() => {
    const onFocus = () => { documentFocusedRef.current = true; };
    const onBlur = () => { documentFocusedRef.current = false; };
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("blur", onBlur); };
  }, []);

  const showMessageNotification = useCallback((candidateId, text) => {
    if (notifPermission !== "granted") return;
    if (String(chatOpen) === String(candidateId) && documentFocusedRef.current) return;
    const candidate = candidates.find(c => String(c.id) === String(candidateId));
    const name = candidate?.name || "A student";
    const notif = new Notification(`New message from ${name}`, {
      body: text.length > 100 ? text.substring(0, 100) + "..." : text,
      tag: `msg-${candidateId}`,
    });
    notif.onclick = () => { window.focus(); openChat(candidateId); notif.close(); };
  }, [notifPermission, chatOpen, candidates]);

  // --- Chat: fetch messages from API ---
  const fetchMessages = useCallback(async (candidateId, since) => {
    if (!authToken) return;
    try {
      const path = since
        ? `/messages/${candidateId}?since=${encodeURIComponent(since)}`
        : `/messages/${candidateId}`;
      const data = await apiGet(path, authToken);
      if (data.success && data.messages) {
        if (since) {
          // Append only new messages
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        } else {
          // Initial load: replace all
          setMessages(data.messages);
        }
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      if (!since) setChatError("Could not load messages. Check your auth token.");
    }
  }, [authToken]);

  // --- Realtime message handlers ---
  const handleRealtimeNewMessage = useCallback((msg) => {
    if (msg.sender_role === "student") {
      if (String(msg.candidate_id) === String(chatOpen)) {
        // Incoming message in active chat — add if not already present
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          if (existingIds.has(msg.id)) return prev;
          return [...prev, msg];
        });
        showMessageNotification(msg.candidate_id, msg.text);
        if (documentFocusedRef.current) markMessagesRead(chatOpen);
      } else {
        // Message for a different conversation — increment unread
        setUnreadCounts(prev => ({
          ...prev,
          [String(msg.candidate_id)]: (prev[String(msg.candidate_id)] || 0) + 1,
        }));
        showMessageNotification(msg.candidate_id, msg.text);
      }
    } else if (msg.sender_role === "recruiter") {
      // Own sent message echo — replace optimistic with real
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        if (existingIds.has(msg.id)) return prev;
        // Try to replace a temp optimistic message
        const tempIdx = prev.findIndex(m => m.id?.startsWith("temp_") && m.text === msg.text);
        if (tempIdx >= 0) {
          const updated = [...prev];
          updated[tempIdx] = { ...msg, from: msg.sender_role };
          return updated;
        }
        return [...prev, { ...msg, from: msg.sender_role }];
      });
    }
  }, [chatOpen, showMessageNotification, markMessagesRead]);

  const handleRealtimeMessageUpdate = useCallback((msg) => {
    setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read_at: msg.read_at } : m));
  }, []);

  const handleRealtimeTypingChange = useCallback((isTyping) => {
    setIsStudentTyping(isTyping);
  }, []);

  const handleRealtimeReconnect = useCallback(() => {
    // Refetch messages for active chat and unread counts after reconnection
    if (chatOpen && authToken) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg?.created_at) {
        fetchMessages(chatOpen, lastMsg.created_at);
      }
    }
    fetchUnreadCounts();
  }, [chatOpen, authToken, messages, fetchMessages, fetchUnreadCounts]);

  const { broadcastTyping } = useRealtimeMessages({
    recruiterId: user?.id,
    activeCandidateId: chatOpen ? String(chatOpen) : null,
    onNewMessage: handleRealtimeNewMessage,
    onMessageUpdate: handleRealtimeMessageUpdate,
    onTypingChange: handleRealtimeTypingChange,
    onReconnect: handleRealtimeReconnect,
  });

  // --- Typing indicator: broadcast via Supabase Realtime ---
  const sendTypingSignal = useCallback(() => {
    broadcastTyping();
  }, [broadcastTyping]);

  // --- File attachment handler ---
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setChatError("Unsupported file type. Use images, PDF, or DOCX.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setChatError("File too large. Maximum 10 MB.");
      return;
    }
    setAttachFile(file);
    setChatError(null);
    if (IMAGE_TYPES.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachFile(null);
    setAttachPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Search
  const handleSearch = async () => {
    if (!jdText.trim()) { setSearchError("Please paste a Job Description first."); return; }
    setSearchError("");
    setSearching(true);
    setSearchResults(null);
    const skillsList = skillsFilter.trim() ? skillsFilter.split(",").map(s => s.trim()).filter(Boolean) : null;
    const payload = {
      query: jdText,
      skills: skillsList,
      languages: selectedLanguages.length ? selectedLanguages : null,
      degree_level: degreeFilter === "Any" ? null : degreeFilter,
      graduation_year: graduationYear === "Any" ? null : graduationYear,
      limit: 10,
      recruiter_id: user?.id,
      recruiter_email: user?.email,
      school_id: profile?.school_id || "64260d45-7131-455e-b720-e17e543289a1",
    };
    try {
      const data = await apiPost("/search", payload, authToken);
      setSearchResults(data.results || []);
    } catch (err) {
      if (err.status === 402 || err.message?.includes("searches per month")) {
        setSearchError("You've reached your free plan search limit this month. Upgrade to Pro for unlimited searches.");
      } else {
        setSearchError(`Search failed: ${err.message}`);
      }
    } finally {
      setSearching(false);
    }
  };

  const toggleLanguage = (lang) => {
    setSelectedLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  // Request CV from search result — calls real API
  const requestCVFromSearch = async (candidateId, matchScore) => {
    if (!user?.id) return;
    setRequestingCV(prev => new Set(prev).add(candidateId));
    try {
      const result = await apiPost("/cv-requests", {
        recruiter_id: user.id,
        candidate_id: candidateId,
        search_query: jdText.substring(0, 200),
        match_score: matchScore,
      }, authToken);
      // Update local usage count
      if (result.success && usageBalances) {
        setUsageBalances(prev => prev ? { ...prev, connections_used: (prev.connections_used || 0) + 1 } : prev);
      }
      // Reload pipeline to reflect new request
      await loadPipeline();
    } catch (err) {
      if (err?.status === 402 || err?.message?.includes("402")) {
        setLimitReachedType("connection");
      } else {
        console.error("Failed to request CV:", err);
      }
    } finally {
      setRequestingCV(prev => { const s = new Set(prev); s.delete(candidateId); return s; });
    }
  };

  const handleCVDownload = (candidateId, format) => {
    // Check local usage before opening link
    if (usageBalances && usageBalances.cv_downloads_used >= usageBalances.cv_downloads_limit) {
      setLimitReachedType("cv_download");
      return;
    }
    // Open CV link — backend authenticates via the token and tracks usage atomically
    const url = `${API_URL}/view_cv/${candidateId}?format=${format}&token=${encodeURIComponent(authToken)}`;
    window.open(url, "_blank");
    // Optimistically update local counter
    if (usageBalances) {
      setUsageBalances(prev => prev ? { ...prev, cv_downloads_used: (prev.cv_downloads_used || 0) + 1 } : prev);
    }
  };

  const pendingRequests = cvRequests.filter(r => r.status === "pending");
  const approvedRequests = cvRequests.filter(r => r.status === "approved");
  const declinedRequests = cvRequests.filter(r => r.status === "declined");
  // Check if a candidate already has an active cv_request from this recruiter
  const hasRequest = (candidateId) => cvRequests.some(r => r.candidate.id === candidateId && (r.status === "pending" || r.status === "approved"));
  const hasApprovedRequest = (candidateId) => cvRequests.some(r => r.candidate.id === candidateId && r.status === "approved");
  const moveCandidate = async (id, direction) => {
    const candidate = candidates.find(c => c.id === id);
    if (!candidate) return;
    const idx = STAGES.indexOf(candidate.stage);
    if (idx === -1) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= STAGES.length) return;
    const newStage = STAGES[newIdx];
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: newStage } : c));
    try {
      await apiPost("/recruiter/update-pipeline", {
        cv_request_id: candidate.cvRequestId,
        pipeline_stage: newStage,
      }, authToken);
    } catch (err) {
      console.error("Failed to update pipeline stage:", err);
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: candidate.stage } : c));
    }
  };
  const moveCandidateToStage = async (id, newStage) => {
    const candidate = candidates.find(c => c.id === id);
    if (!candidate || candidate.stage === newStage) return;
    const oldStage = candidate.stage;
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: newStage } : c));
    try {
      await apiPost("/recruiter/update-pipeline", {
        cv_request_id: candidate.cvRequestId,
        pipeline_stage: newStage,
      }, authToken);
    } catch (err) {
      console.error("Failed to update pipeline stage:", err);
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: oldStage } : c));
    }
  };
  const openRejectDialog = (id) => {
    const candidate = candidates.find(c => c.id === id);
    if (!candidate) return;
    const isEarly = EARLY_STAGES.includes(candidate.stage);
    setRejectDialog({ id, name: candidate.name, stage: candidate.stage });
    setRejectMessage(isEarly ? DEFAULT_REJECTION_MSG : "");
  };
  const confirmReject = async () => {
    if (!rejectDialog) return;
    const { id } = rejectDialog;
    const candidate = candidates.find(c => c.id === id);
    if (!candidate) return;
    const msg = rejectMessage.trim();
    if (!msg) return; // require a message
    setRejectSending(true);
    const oldStage = candidate.stage;
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: REJECTED } : c));
    try {
      // Send the rejection message to the student
      await apiPost("/messages", { candidate_id: String(id), text: msg }, authToken);
      // Update pipeline stage
      await apiPost("/recruiter/update-pipeline", {
        cv_request_id: candidate.cvRequestId,
        pipeline_stage: "Rejected",
      }, authToken);
      setRejectDialog(null);
      setRejectMessage("");
    } catch (err) {
      console.error("Failed to reject candidate:", err);
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, stage: oldStage } : c));
    } finally {
      setRejectSending(false);
    }
  };
  const sendReport = async (id) => {
    const candidate = candidates.find(c => c.id === id);
    if (!candidate) return;
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, reportSent: true } : c));
    setReportLog(prev => [...prev, {
      id: prev.length + 1, candidateId: id, school: candidate.school,
      stage: candidate.stage, ts: new Date().toISOString().split("T")[0], type: "progress",
    }]);
    try {
      await apiPost("/recruiter/update-pipeline", {
        cv_request_id: candidate.cvRequestId,
        report_sent: true,
      }, authToken);
    } catch (err) {
      console.error("Failed to send report:", err);
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, reportSent: false } : c));
    }
  };
  const requestCVFromCandidate = async (id) => {
    if (!user?.id) return;
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, cvRequested: true } : c));
    try {
      const cand = candidates.find(c => c.id === id);
      const result = await apiPost("/cv-requests", {
        recruiter_id: user.id,
        candidate_id: String(id),
        match_score: cand?.competencyMatch ? cand.competencyMatch / 100 : null,
      }, authToken);
      if (result.success && usageBalances) {
        setUsageBalances(prev => prev ? { ...prev, connections_used: (prev.connections_used || 0) + 1 } : prev);
      }
    } catch (err) {
      if (err?.status === 402 || err?.message?.includes("402")) {
        setLimitReachedType("connection");
      } else {
        console.error("Failed to request CV:", err);
      }
      setCandidates(prev => prev.map(c => c.id === id ? { ...c, cvRequested: false } : c));
    }
  };
  // Reset typing state when chat changes (realtime handles message delivery)
  useEffect(() => {
    setIsStudentTyping(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  }, [chatOpen]);

  // Polling fallback for new messages (realtime not reliably delivering)
  useEffect(() => {
    if (!chatOpen || !authToken) return;
    const interval = setInterval(() => {
      setMessages(prev => {
        const nonOptimistic = prev.filter(m => !m.id?.startsWith("temp_"));
        const last = nonOptimistic[nonOptimistic.length - 1];
        if (last?.created_at) {
          fetchMessages(chatOpen, last.created_at);
        }
        return prev;
      });
    }, 4000);
    return () => clearInterval(interval);
  }, [chatOpen, authToken, fetchMessages]);

  // Poll unread counts periodically
  useEffect(() => {
    if (!authToken) return;
    const interval = setInterval(fetchUnreadCounts, 15000);
    return () => clearInterval(interval);
  }, [authToken, fetchUnreadCounts]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const openChat = async (candidateId) => {
    setChatOpen(candidateId);
    setActiveView("kanban");
    setChatError(null);
    setMessages([]);
    clearAttachment();
    if (authToken) {
      setChatLoading(true);
      await fetchMessages(candidateId);
      setChatLoading(false);
      markMessagesRead(candidateId);
    }
  };

  const sendMessage = async () => {
    if ((!msgDraft.trim() && !attachFile) || !chatOpen) return;
    const text = msgDraft.trim();
    setMsgDraft("");
    const file = attachFile;
    const hasFile = !!file;
    clearAttachment();

    // Optimistic update — show message immediately
    const tempId = `temp_${Date.now()}`;
    const optimisticMsg = {
      id: tempId, candidate_id: String(chatOpen), candidateId: chatOpen,
      sender_role: "recruiter", from: "recruiter", text: text || (hasFile ? `📎 ${file.name}` : ""),
      created_at: new Date().toISOString(), ts: new Date().toLocaleString(),
      ...(hasFile && { file_name: file.name, file_type: file.type, file_size: file.size }),
    };
    setMessages(prev => [...prev, optimisticMsg]);

    if (authToken) {
      try {
        let data;
        if (hasFile) {
          setUploading(true);
          const formData = new FormData();
          formData.append("candidate_id", String(chatOpen));
          formData.append("text", text);
          formData.append("file", file);
          const url = `${API_URL}/messages/upload`;
          const res = await fetch(url, {
            method: "POST",
            headers: { Authorization: `Bearer ${authToken}` },
            body: formData,
          });
          if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
          data = await res.json();
          setUploading(false);
        } else {
          data = await apiPost("/messages", { candidate_id: String(chatOpen), text }, authToken);
        }
        if (data.success && data.message) {
          setMessages(prev => prev.map(m => m.id === tempId ? { ...data.message, from: data.message.sender_role } : m));
        }
      } catch (err) {
        console.error("Failed to send message:", err);
        if (err.status === 403) {
          setMessages(prev => prev.filter(m => m.id !== tempId));
          setChatError("You need to download this student's CV before you can message them.");
        } else {
          setChatError("Failed to send. Check your connection.");
        }
        setUploading(false);
      }
    }
  };

  const searchChatMessages = async () => {
    const q = msgSearchQuery.trim();
    if (!q || q.length < 2 || !authToken) return;
    setMsgSearching(true);
    try {
      const data = await apiGet(`/messages/search?q=${encodeURIComponent(q)}`, authToken);
      setMsgSearchResults(data.results || []);
    } catch (err) {
      console.error("Message search failed:", err);
    } finally {
      setMsgSearching(false);
    }
  };

  const activeCandidates = candidates.filter(c => c.stage !== REJECTED);
  const rejectedCandidates = candidates.filter(c => c.stage === REJECTED);
  const chatCandidate = candidates.find(c => c.id === chatOpen);
  const chatMessages = messages.filter(m =>
    String(m.candidate_id) === String(chatOpen) || m.candidateId === chatOpen
  );
  const totalUnread = Object.values(unreadCounts).reduce((sum, n) => sum + n, 0);
  const stats = {
    total: candidates.length,
    active: activeCandidates.length,
    inPipeline: candidates.filter(c => !["Downloaded", "Hired", REJECTED].includes(c.stage)).length,
    hired: candidates.filter(c => c.stage === "Hired").length,
    reported: candidates.filter(c => c.reportSent).length,
  };
  const NAV = [
    { key: "search", label: "🔍 Search" },
    { key: "saved", label: `📡 My Searches${savedSearches.filter(s => s.active).length > 0 ? ` (${savedSearches.filter(s => s.active).length})` : ""}` },
    { key: "incoming", label: "🔔 Incoming" },
    { key: "kanban", label: "Pipeline", unread: totalUnread },
    { key: "tracker", label: "CV Tracker" },
    { key: "reports", label: "Reports" },
  ];
  return (
    <div style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", backgroundColor: COLORS.bg, minHeight: "100vh", color: COLORS.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      {/* Payment success banner */}
      {paymentSuccess && (
        <div style={{
          backgroundColor: COLORS.greenLight, color: COLORS.green,
          padding: "12px 28px", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8, fontSize: 14, fontWeight: 600,
          borderBottom: `1px solid ${COLORS.green}22`,
        }}>
          <span style={{ fontSize: 18 }}>&#10003;</span>
          Payment successful — you're now on Pro! Enjoy unlimited search and 100 CV requests.
          <button onClick={() => setPaymentSuccess(false)} style={{
            background: "none", border: "none", color: COLORS.green,
            cursor: "pointer", fontSize: 18, fontWeight: 700, marginLeft: 8,
            fontFamily: "inherit",
          }}>&times;</button>
        </div>
      )}
      {/* Header */}
      <header style={{
        backgroundColor: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`,
        padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6, backgroundColor: COLORS.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: 14,
            }}>R</div>
            <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: "-0.02em" }}>Relay</span>
            <Badge bg={COLORS.surfaceAlt} color={COLORS.textMuted} small>Recruiter</Badge>
          </div>
          <nav className="header-nav" style={{ display: "flex", gap: 4 }}>
            {NAV.map(n => (
              <button key={n.key} onClick={() => setActiveView(n.key)} style={{
                padding: "6px 14px", borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600,
                fontFamily: "inherit", cursor: "pointer", transition: "all 0.15s", position: "relative",
                backgroundColor: activeView === n.key ? COLORS.accent : "transparent",
                color: activeView === n.key ? "#fff" : COLORS.textSecondary,
              }}>
                {n.label}
                {n.key === "incoming" && pendingRequests.length > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4, minWidth: 18, height: 18,
                    borderRadius: 9, backgroundColor: COLORS.red, color: "#fff",
                    fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center",
                    justifyContent: "center", padding: "0 5px",
                  }}>{pendingRequests.length}</span>
                )}
                {n.key === "kanban" && totalUnread > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -4, minWidth: 18, height: 18,
                    borderRadius: 9, backgroundColor: COLORS.red, color: "#fff",
                    fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center",
                    justifyContent: "center", padding: "0 5px",
                  }}>{totalUnread}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: COLORS.textSecondary }}>
          {/* Tier badge */}
          {profile?.tier && profile.role === "recruiter" && (
            <Badge
              bg={profile.tier === "pro" ? COLORS.accentLight : COLORS.surfaceAlt}
              color={profile.tier === "pro" ? COLORS.accent : COLORS.textMuted}
              small
            >
              {profile.tier === "pro" ? "Pro" : "Free"}
            </Badge>
          )}
          {/* Usage counters */}
          {usageBalances && (
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{
                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                backgroundColor: (usageBalances.cv_downloads_used / usageBalances.cv_downloads_limit) > 0.8 ? COLORS.orangeLight
                  : usageBalances.cv_downloads_used >= usageBalances.cv_downloads_limit ? COLORS.redLight : COLORS.surfaceAlt,
                color: (usageBalances.cv_downloads_used / usageBalances.cv_downloads_limit) > 0.8 ? COLORS.orange
                  : usageBalances.cv_downloads_used >= usageBalances.cv_downloads_limit ? COLORS.red : COLORS.textSecondary,
              }}>
                CVs: {usageBalances.cv_downloads_used}/{usageBalances.cv_downloads_limit}
              </div>
              <div style={{
                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                backgroundColor: (usageBalances.connections_used / usageBalances.connections_limit) > 0.8 ? COLORS.orangeLight
                  : usageBalances.connections_used >= usageBalances.connections_limit ? COLORS.redLight : COLORS.surfaceAlt,
                color: (usageBalances.connections_used / usageBalances.connections_limit) > 0.8 ? COLORS.orange
                  : usageBalances.connections_used >= usageBalances.connections_limit ? COLORS.red : COLORS.textSecondary,
              }}>
                Connections: {usageBalances.connections_used}/{usageBalances.connections_limit}
              </div>
            </div>
          )}
          {profile?.subscription_status === "active" && (
            <button
              onClick={async () => {
                try {
                  const data = await apiPost("/payments/create-portal-session", {}, authToken);
                  if (data.portal_url) window.location.href = data.portal_url;
                } catch (err) {
                  // Silently fail — not critical
                }
              }}
              style={{
                padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface,
                color: COLORS.textSecondary, fontFamily: "inherit", cursor: "pointer",
              }}
            >
              Manage Billing
            </button>
          )}
          <span>{profile?.company_name || "Company"}</span>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", backgroundColor: COLORS.accentLight,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 13, color: COLORS.accent,
          }}>{(profile?.full_name || profile?.email || "U").charAt(0).toUpperCase()}</div>
          {pushNotifications.isSupported && (
            <button
              onClick={() => {
                if (pushNotifications.status === "subscribed") {
                  pushNotifications.unsubscribe();
                } else {
                  pushNotifications.subscribe();
                }
              }}
              title={pushNotifications.status === "subscribed" ? "Notifications on — click to disable" : "Enable push notifications"}
              style={{
                width: 32, height: 32, borderRadius: 6, border: `1px solid ${COLORS.border}`,
                backgroundColor: pushNotifications.status === "subscribed" ? COLORS.accentLight : COLORS.surface,
                color: pushNotifications.status === "subscribed" ? COLORS.accent : COLORS.textMuted,
                fontSize: 16, cursor: pushNotifications.status === "denied" ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "inherit", opacity: pushNotifications.status === "denied" ? 0.4 : 1,
              }}
              disabled={pushNotifications.status === "denied"}
            >
              {pushNotifications.status === "subscribed" ? "\uD83D\uDD14" : "\uD83D\uDD15"}
            </button>
          )}
          <button onClick={signOut} style={{
            padding: "5px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface, color: COLORS.textSecondary, fontSize: 12,
            fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
          }}>Log out</button>
        </div>
      </header>
      {/* Push notification opt-in banner */}
      {showPushPrompt && pushNotifications.status !== "subscribed" && pushNotifications.status !== "denied" && (
        <div style={{
          backgroundColor: COLORS.accentLight, padding: "10px 28px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          borderBottom: `1px solid ${COLORS.border}`, fontSize: 13,
        }}>
          <span style={{ color: COLORS.text }}>
            Enable push notifications to get alerts when students message you.
          </span>
          <button onClick={async () => {
            await pushNotifications.subscribe();
            setShowPushPrompt(false);
          }} style={{
            padding: "5px 14px", borderRadius: 6, border: "none",
            backgroundColor: COLORS.accent, color: "#fff", fontSize: 12,
            fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
          }}>Enable</button>
          <button onClick={() => {
            setShowPushPrompt(false);
            sessionStorage.setItem("push-prompt-dismissed", "1");
          }} style={{
            padding: "5px 10px", borderRadius: 6, border: "none",
            backgroundColor: "transparent", color: COLORS.textMuted, fontSize: 12,
            fontFamily: "inherit", cursor: "pointer",
          }}>Not now</button>
        </div>
      )}
      <div className="main-layout" style={{ display: "flex", maxWidth: 1400, margin: "0 auto", padding: "20px 28px", gap: 20 }}>
        {/* Main Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Stats Row */}
          <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 24 }}>
            {[
              { label: "CVs Downloaded", value: stats.total, icon: "📥" },
              { label: "Active Pipeline", value: stats.active, icon: "📋" },
              { label: "In Process", value: stats.inPipeline, icon: "⏳" },
              { label: "Hired", value: stats.hired, icon: "✅" },
              { label: "Reported to Schools", value: stats.reported, icon: "📤" },
            ].map((s, i) => (
              <Card key={i} style={{ padding: 16 }}>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>{s.icon} {s.label}</div>
                <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em" }}>{s.value}</div>
              </Card>
            ))}
          </div>
          {/* Upgrade Banner for free-tier recruiters */}
          <UpgradeBanner usage={usageBalances} />
          {/* SEARCH VIEW */}
          {activeView === "search" && (
            <>
              <SectionTitle sub="Find matching candidates using a job description and filters">
                Search Talent Pool
              </SectionTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Degree Level</label>
                  <select value={degreeFilter} onChange={e => setDegreeFilter(e.target.value)} style={{
                    padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                    fontSize: 14, fontFamily: "inherit", backgroundColor: COLORS.surface, color: COLORS.text, width: "100%", outline: "none",
                  }}>
                    {DEGREE_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Graduation Year</label>
                  <select value={graduationYear} onChange={e => setGraduationYear(e.target.value)} style={{
                    padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                    fontSize: 14, fontFamily: "inherit", backgroundColor: COLORS.surface, color: COLORS.text, width: "100%", outline: "none",
                  }}>
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Languages</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {LANGUAGE_OPTIONS.map(lang => (
                      <button key={lang} onClick={() => toggleLanguage(lang)} style={{
                        padding: "5px 12px", borderRadius: 6, fontSize: 13, fontWeight: 500, fontFamily: "inherit", cursor: "pointer", transition: "all 0.15s",
                        border: `1px solid ${selectedLanguages.includes(lang) ? COLORS.accent : COLORS.border}`,
                        backgroundColor: selectedLanguages.includes(lang) ? COLORS.accentLight : COLORS.surface,
                        color: selectedLanguages.includes(lang) ? COLORS.accent : COLORS.textSecondary,
                      }}>{lang}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Skills (comma-separated)</label>
                  <input type="text" value={skillsFilter} onChange={e => setSkillsFilter(e.target.value)}
                    placeholder="e.g. Python, Excel, Project Management"
                    style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", backgroundColor: COLORS.surface, color: COLORS.text, width: "100%", outline: "none", boxSizing: "border-box" }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 6 }}>Job Description / Requirements</label>
                <textarea value={jdText} onChange={e => setJdText(e.target.value)}
                  placeholder="Paste a job description or describe the ideal candidate..."
                  style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, fontFamily: "inherit", backgroundColor: COLORS.surface, color: COLORS.text, width: "100%", outline: "none", minHeight: 140, resize: "vertical", boxSizing: "border-box" }}
                />
              </div>
              <Button variant="primary" onClick={handleSearch} style={{ opacity: searching ? 0.7 : 1, cursor: searching ? "wait" : "pointer" }}>
                {searching ? "Searching..." : "Search Candidates"}
              </Button>
              {searchError && (
                <div style={{ padding: "10px 14px", borderRadius: 8, marginTop: 16, backgroundColor: COLORS.redLight, color: COLORS.red, fontSize: 13 }}>{searchError}</div>
              )}
              {searchResults && (
                <div style={{ marginTop: 24 }}>
                  {searchResults.length === 0 ? (
                    <Card style={{ textAlign: "center", padding: 40 }}>
                      <div style={{ fontSize: 14, color: COLORS.orange }}>No matching candidates found.</div>
                    </Card>
                  ) : (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <p style={{ fontSize: 14, fontWeight: 600, color: COLORS.green, margin: 0 }}>
                          Found {searchResults.length} matching candidates
                        </p>
                        {/* Keep this search open toggle */}
                        {lastSearchSaved ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 8, backgroundColor: COLORS.greenLight, color: COLORS.green, fontSize: 13, fontWeight: 600 }}>
                            <span>&#10003;</span> Search saved — you'll be notified of new matches
                          </div>
                        ) : (
                          <button
                            onClick={usageBalances?.saved_searches_limit === 0 && usageBalances?.tier === "free" ? () => setShowPricing(true) : handleSaveSearch}
                            disabled={savingSearch}
                            style={{
                              display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8,
                              fontSize: 13, fontWeight: 600, cursor: savingSearch ? "wait" : "pointer", border: "none", fontFamily: "inherit",
                              backgroundColor: usageBalances?.tier === "free" ? COLORS.surfaceAlt : COLORS.accentLight,
                              color: usageBalances?.tier === "free" ? COLORS.textMuted : COLORS.accent,
                              opacity: savingSearch ? 0.7 : 1,
                            }}
                          >
                            <span style={{ fontSize: 16 }}>📡</span>
                            {savingSearch ? "Saving..." : usageBalances?.tier === "free" ? "Keep open (Pro)" : "Keep this search open"}
                          </button>
                        )}
                      </div>
                      {searchResults.map(candidate => {
                        const similarity = candidate.similarity || 0;
                        // Prefer the calibrated LLM fit score; fall back to cosine.
                        const hasFit = typeof candidate.fit_score === "number";
                        const scoreVal = hasFit ? candidate.fit_score / 100 : similarity;
                        const scorePct = `${Math.round(scoreVal * 100)}%`;
                        const isExpanded = expandedSearchId === candidate.id;
                        const alreadyRequested = hasRequest(candidate.id);
                        const cvApproved = hasApprovedRequest(candidate.id);
                        const isRequesting = requestingCV.has(candidate.id);
                        return (
                          <Card key={candidate.id} style={{ marginBottom: 10, padding: 0, overflow: "hidden" }}>
                            <div onClick={() => setExpandedSearchId(isExpanded ? null : candidate.id)}
                              style={{ padding: "14px 18px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>{candidate.name}</span>
                                <Badge bg={scoreVal >= 0.7 ? COLORS.greenLight : scoreVal >= 0.4 ? COLORS.orangeLight : COLORS.surfaceAlt}
                                  color={scoreVal >= 0.7 ? COLORS.green : scoreVal >= 0.4 ? COLORS.orange : COLORS.textMuted} small>
                                  {scorePct} match
                                </Badge>
                              </div>
                              <span style={{ fontSize: 14, color: COLORS.textMuted }}>{isExpanded ? "−" : "+"}</span>
                            </div>
                            {isExpanded && (
                              <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${COLORS.borderLight}`, paddingTop: 14 }}>
                                {candidate.fit_rationale && (
                                  <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 8 }}>
                                    <strong>Why this match:</strong> {candidate.fit_rationale}
                                  </p>
                                )}
                                {candidate.matched?.length > 0 && (
                                  <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 4 }}>
                                    <strong>Meets:</strong> {candidate.matched.join(", ")}
                                  </p>
                                )}
                                {candidate.missing?.length > 0 && (
                                  <p style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 8 }}>
                                    <strong>Gaps:</strong> {candidate.missing.join(", ")}
                                  </p>
                                )}
                                <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 12 }}>
                                  <strong>Experience:</strong> {candidate.total_experience_years ?? "N/A"} years
                                </p>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                  {candidate.pdf_url && candidate.pdf_url !== "storage_temporarily_disabled" && (<>
                                    <button onClick={cvApproved ? () => handleCVDownload(candidate.id, "docx") : undefined}
                                      title={cvApproved ? "Download DOCX" : "Request CV access first — student approval required"}
                                      style={{ display: "inline-block", padding: "8px 16px", borderRadius: 6, backgroundColor: cvApproved ? COLORS.accentLight : "#e5e5e5", color: cvApproved ? COLORS.accent : "#999", fontSize: 13, fontWeight: 600, textDecoration: "none", border: "none", cursor: cvApproved ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: cvApproved ? 1 : 0.7 }}>
                                      &#11015; DOCX
                                    </button>
                                    <button onClick={cvApproved ? () => handleCVDownload(candidate.id, "pdf") : undefined}
                                      title={cvApproved ? "Download PDF" : "Request CV access first — student approval required"}
                                      style={{ display: "inline-block", padding: "8px 16px", borderRadius: 6, backgroundColor: cvApproved ? "transparent" : "#e5e5e5", color: cvApproved ? COLORS.accent : "#999", fontSize: 13, fontWeight: 600, textDecoration: "none", border: cvApproved ? `1px solid ${COLORS.accent}` : "1px solid #ccc", cursor: cvApproved ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: cvApproved ? 1 : 0.7 }}>
                                      &#11015; PDF
                                    </button>
                                  </>)}
                                  {alreadyRequested ? (
                                    <Badge bg={COLORS.orangeLight} color={COLORS.orange}>CV already requested</Badge>
                                  ) : (
                                    <Button variant="primary" small onClick={() => requestCVFromSearch(candidate.id, scoreVal)}
                                      style={{ opacity: isRequesting ? 0.6 : 1 }}>
                                      {isRequesting ? "Requesting..." : "📩 Request CV"}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {/* MY SEARCHES VIEW — Persistent/saved searches */}
          {activeView === "saved" && (
            <>
              <SectionTitle sub="Your open searches — get notified when new matching students appear">
                My Searches
              </SectionTitle>
              {savedSearchesLoading ? (
                <Card style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>Loading saved searches...</div>
                </Card>
              ) : savedSearches.length === 0 ? (
                <Card style={{ textAlign: "center", padding: 60 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📡</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No saved searches yet</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>
                    Run a search and click "Keep this search open" to get notified when new matching students appear.
                  </div>
                  <Button variant="primary" onClick={() => setActiveView("search")}>Go to Search</Button>
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {savedSearches.map(s => (
                    <Card key={s.id} style={{ padding: 0, overflow: "hidden", borderLeft: `4px solid ${s.active ? COLORS.accent : COLORS.border}` }}>
                      <div style={{ padding: "14px 18px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
                              {s.query_text.length > 80 ? s.query_text.substring(0, 80) + "..." : s.query_text}
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {s.filters?.skills?.length > 0 && (
                                <Badge bg={COLORS.surfaceAlt} color={COLORS.textSecondary} small>
                                  Skills: {s.filters.skills.join(", ")}
                                </Badge>
                              )}
                              {s.filters?.languages?.length > 0 && (
                                <Badge bg={COLORS.surfaceAlt} color={COLORS.textSecondary} small>
                                  {s.filters.languages.join(", ")}
                                </Badge>
                              )}
                              {s.filters?.degree_level && (
                                <Badge bg={COLORS.surfaceAlt} color={COLORS.textSecondary} small>
                                  {s.filters.degree_level}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Badge
                            bg={s.active ? COLORS.greenLight : COLORS.surfaceAlt}
                            color={s.active ? COLORS.green : COLORS.textMuted}
                            small
                          >
                            {s.active ? "Active" : "Paused"}
                          </Badge>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div style={{ display: "flex", gap: 16, fontSize: 12, color: COLORS.textMuted }}>
                            <span>Created {new Date(s.created_at).toLocaleDateString()}</span>
                            <span>{s.matches_sent || 0} matches sent</span>
                            {s.pending_matches > 0 && (
                              <span style={{ color: COLORS.accent, fontWeight: 600 }}>{s.pending_matches} new matches</span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button small variant={s.active ? "default" : "primary"} onClick={() => handleToggleSavedSearch(s.id, s.active)}>
                              {s.active ? "Pause" : "Resume"}
                            </Button>
                            <Button small variant="danger" onClick={() => handleDeleteSavedSearch(s.id)}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
          {/* INCOMING VIEW — Real CV request statuses */}
          {activeView === "incoming" && (
            <>
              <SectionTitle sub="Track your CV requests — pending student responses and approved access">
                CV Request Status
              </SectionTitle>
              {pipelineLoading ? (
                <Card style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>Loading requests...</div>
                </Card>
              ) : cvRequests.length === 0 ? (
                <Card style={{ textAlign: "center", padding: 60 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📩</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>No CV requests yet</div>
                  <div style={{ fontSize: 13, color: COLORS.textMuted }}>
                    Search for candidates and request their CVs to get started.
                  </div>
                </Card>
              ) : (
                <>
                  {pendingRequests.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.orange, marginBottom: 10 }}>
                        ⏳ Awaiting student response — {pendingRequests.length}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {pendingRequests.map(r => (
                          <Card key={r.id} style={{ padding: 14, borderLeft: `4px solid ${COLORS.orange}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600 }}>{r.candidate.name}</div>
                                <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                                  {r.search_query ? `Searched: ${r.search_query.substring(0, 60)}...` : "CV requested"}
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {r.match_score && (
                                  <Badge bg={COLORS.accentLight} color={COLORS.accent} small>{Math.round(r.match_score * 100)}% match</Badge>
                                )}
                                <Badge bg={COLORS.orangeLight} color={COLORS.orange} small>Pending</Badge>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  {approvedRequests.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.green, marginBottom: 10 }}>
                        ✓ CV received — {approvedRequests.length}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {approvedRequests.map(r => (
                          <Card key={r.id} style={{ padding: 14, borderLeft: `4px solid ${COLORS.green}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 600 }}>{r.candidate.name}</div>
                                <div style={{ fontSize: 12, color: COLORS.textSecondary }}>
                                  Approved {r.responded_at ? new Date(r.responded_at).toLocaleDateString() : ""}
                                </div>
                              </div>
                              <Badge bg={COLORS.greenLight} color={COLORS.green} small>In Pipeline</Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                  {declinedRequests.length > 0 && (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 10 }}>
                        Declined — {declinedRequests.length}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {declinedRequests.map(r => (
                          <Card key={r.id} style={{ padding: 14, opacity: 0.6 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ fontSize: 14, fontWeight: 600 }}>{r.candidate.name}</div>
                              <Badge bg={COLORS.redLight} color={COLORS.red} small>Declined</Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {/* KANBAN VIEW */}
          {activeView === "kanban" && (
            <>
              <SectionTitle sub="Move candidates through your hiring pipeline — status updates push to schools & students">
                Candidate Pipeline
              </SectionTitle>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={(event) => {
                  setDraggedCandidate(event.active.data.current?.candidate || null);
                }}
                onDragEnd={(event) => {
                  setDraggedCandidate(null);
                  const { active, over } = event;
                  if (!over) return;
                  const candidate = active.data.current?.candidate;
                  if (!candidate) return;
                  const targetStage = over.id.replace("stage-", "");
                  if (STAGES.includes(targetStage) && targetStage !== candidate.stage) {
                    moveCandidateToStage(candidate.id, targetStage);
                  }
                }}
                onDragCancel={() => setDraggedCandidate(null)}
              >
                <div className="kanban-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${STAGES.length}, 1fr)`, gap: 12, marginBottom: 24 }}>
                  {STAGES.map(stage => {
                    const inStage = activeCandidates.filter(c => c.stage === stage);
                    const badge = stageBadge(stage);
                    return (
                      <DroppableColumn key={stage} stage={stage}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "0 2px" }}>
                          <Badge bg={badge.bg} color={badge.color}>{stage}</Badge>
                          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted }}>{inStage.length}</span>
                        </div>
                        {inStage.map(c => (
                          <DraggableKanbanCard key={c.id} candidate={c} onMove={moveCandidate}
                            onReject={openRejectDialog} onChat={openChat} onReport={sendReport}
                            onRequestCV={requestCVFromCandidate} onDownloadCV={handleCVDownload}
                            unreadCount={unreadCounts[String(c.id)] || 0} />
                        ))}
                      </DroppableColumn>
                    );
                  })}
                </div>
                <DragOverlay>
                  {draggedCandidate ? (
                    <div style={{ opacity: 0.9, transform: "rotate(2deg)", pointerEvents: "none" }}>
                      <KanbanCard candidate={draggedCandidate} onMove={() => {}} onReject={() => {}}
                        onChat={() => {}} onReport={() => {}} onRequestCV={() => {}} onDownloadCV={() => {}}
                        unreadCount={0} />
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
              {rejectedCandidates.length > 0 && (
                <Card style={{ opacity: 0.7 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>
                    Rejected ({rejectedCandidates.length})
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {rejectedCandidates.map(c => (
                      <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Badge bg={COLORS.redLight} color={COLORS.red} small>{c.name}</Badge>
                        <button onClick={() => moveCandidateToStage(c.id, "Downloaded")} title="Move back to Downloaded" style={{
                          background: "none", border: "none", cursor: "pointer", fontSize: 13,
                          color: COLORS.textMuted, padding: "0 2px", lineHeight: 1,
                        }}>↩</button>
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
          {/* CV TRACKER VIEW */}
          {activeView === "tracker" && (
            <>
              <SectionTitle sub="Every CV downloaded from Relay — tracked before it enters your ATS">
                CV Download Tracker
              </SectionTitle>
              <Card style={{ padding: 0, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.surfaceAlt }}>
                      {["Candidate", "School", "Programme", "Downloaded", "Match", "Stage", "Reported", "Actions"].map(h => (
                        <th key={h} style={{
                          padding: "10px 14px", textAlign: "left", fontWeight: 600,
                          color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase",
                          letterSpacing: "0.04em", borderBottom: `1px solid ${COLORS.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map(c => {
                      const badge = stageBadge(c.stage);
                      return (
                        <tr key={c.id} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.surfaceAlt}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}>
                          <td style={{ padding: "10px 14px", fontWeight: 600 }}>{c.name}</td>
                          <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{c.school}</td>
                          <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{c.programme}</td>
                          <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{c.downloaded}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ fontWeight: 700, color: c.competencyMatch >= 90 ? COLORS.green : c.competencyMatch >= 80 ? COLORS.orange : COLORS.textSecondary }}>
                              {c.competencyMatch}%
                            </span>
                          </td>
                          <td style={{ padding: "10px 14px" }}><Badge bg={badge.bg} color={badge.color} small>{c.stage}</Badge></td>
                          <td style={{ padding: "10px 14px" }}>
                            {c.reportSent
                              ? <Badge bg={COLORS.greenLight} color={COLORS.green} small>✓ Sent</Badge>
                              : <Badge bg={COLORS.surfaceAlt} color={COLORS.textMuted} small>Pending</Badge>}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <span style={{ position: "relative", display: "inline-block" }}>
                                <Button small variant="ghost" onClick={() => openChat(c.id)}>💬</Button>
                                {(unreadCounts[String(c.id)] || 0) > 0 && (
                                  <span style={{
                                    position: "absolute", top: -6, right: -6, minWidth: 16, height: 16,
                                    borderRadius: 8, backgroundColor: COLORS.red, color: "#fff",
                                    fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center",
                                    justifyContent: "center", padding: "0 4px",
                                  }}>{unreadCounts[String(c.id)]}</span>
                                )}
                              </span>
                              {!c.reportSent && <Button small variant="ghost" onClick={() => sendReport(c.id)}>📤</Button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            </>
          )}
          {/* REPORTS VIEW */}
          {activeView === "reports" && (
            <>
              <SectionTitle sub="Status updates sent back to school career services dashboards and student apps">
                Progress Reports
              </SectionTitle>
              <div className="reports-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                <Card>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: COLORS.textSecondary }}>
                    📊 Reporting Summary
                  </div>
                  <div className="reports-summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.reported}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>Reports sent</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.total - stats.reported}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>Pending reports</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{new Set(candidates.filter(c => c.reportSent).map(c => c.school)).size}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>Schools notified</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.reported}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>Students updated</div>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: COLORS.textSecondary }}>
                    🔄 Data Flow
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <Badge bg={COLORS.accentLight} color={COLORS.accent} small>Recruiter</Badge>
                      <span>→</span>
                      <Badge bg={COLORS.purpleLight} color={COLORS.purple} small>Careers Dashboard</Badge>
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>
                      Stage changes, outcomes, and feedback are sent to the school's career services team.
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <Badge bg={COLORS.accentLight} color={COLORS.accent} small>Recruiter</Badge>
                      <span>→</span>
                      <Badge bg={COLORS.greenLight} color={COLORS.green} small>Student App</Badge>
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                      Students receive real-time status updates and messages via their mobile app.
                    </div>
                  </div>
                </Card>
              </div>
              <Card style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13, fontWeight: 600 }}>
                  Report Log
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.surfaceAlt }}>
                      {["Date", "Candidate", "School", "Stage Reported", "Sent To"].map(h => (
                        <th key={h} style={{
                          padding: "10px 14px", textAlign: "left", fontWeight: 600,
                          color: COLORS.textMuted, fontSize: 11, textTransform: "uppercase",
                          letterSpacing: "0.04em", borderBottom: `1px solid ${COLORS.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportLog.sort((a, b) => b.ts.localeCompare(a.ts)).map(r => {
                      const cand = candidates.find(c => c.id === r.candidateId);
                      const badge = stageBadge(r.stage);
                      return (
                        <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                          <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.ts}</td>
                          <td style={{ padding: "10px 14px", fontWeight: 600 }}>{cand?.name || r.candidateName || "—"}</td>
                          <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.school || ""}</td>
                          <td style={{ padding: "10px 14px" }}><Badge bg={badge.bg} color={badge.color} small>{r.stage}</Badge></td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <Badge bg={COLORS.purpleLight} color={COLORS.purple} small>Careers</Badge>
                              <Badge bg={COLORS.greenLight} color={COLORS.green} small>Student</Badge>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
              <div style={{ marginTop: 16 }}>
                <Button variant="primary" onClick={() => {
                  candidates.filter(c => !c.reportSent && c.stage !== REJECTED).forEach(c => sendReport(c.id));
                }}>
                  📤 Send All Pending Reports
                </Button>
              </div>
            </>
          )}
        </div>
        {/* Chat Panel */}
        <div className="chat-panel-wrapper" style={{
          width: chatOpen ? 320 : 0, flexShrink: 0, transition: "width 0.2s",
          overflow: "hidden",
        }}>
          {chatOpen && chatCandidate && (
            <div style={{
              width: 320, position: "sticky", top: 80,
              backgroundColor: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`,
              display: "flex", flexDirection: "column", height: "calc(100vh - 120px)",
            }}>
              {/* Chat Header */}
              <div style={{
                padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}`,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{chatCandidate.name}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                    {chatCandidate.school}
                  </div>
                </div>
                <Button small variant="ghost" onClick={() => { setChatOpen(null); clearAttachment(); }}>✕</Button>
              </div>
              {notifPermission === "denied" && (
                <div style={{
                  padding: "6px 12px", backgroundColor: COLORS.orangeLight,
                  color: COLORS.orange, fontSize: 11, textAlign: "center",
                  borderBottom: `1px solid ${COLORS.border}`,
                }}>
                  Enable browser notifications to get alerts for new messages
                </div>
              )}
              {/* Message Search */}
              <div style={{ padding: "8px 12px", borderBottom: `1px solid ${COLORS.borderLight}` }}>
                <input
                  value={msgSearchQuery}
                  onChange={e => { setMsgSearchQuery(e.target.value); if (!e.target.value) setMsgSearchResults([]); }}
                  onKeyDown={e => e.key === "Enter" && searchChatMessages()}
                  placeholder="Search messages..."
                  style={{
                    width: "100%", padding: "6px 10px", borderRadius: 6,
                    border: `1px solid ${COLORS.border}`, fontSize: 12, fontFamily: "inherit",
                    outline: "none", backgroundColor: COLORS.bg,
                  }}
                />
                {msgSearchResults.length > 0 && (
                  <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4 }}>
                      {msgSearchResults.length} result{msgSearchResults.length !== 1 ? "s" : ""}
                    </div>
                    {msgSearchResults.map(r => (
                      <div key={r.id} onClick={() => {
                        const cand = candidates.find(c => String(c.id) === String(r.candidate_id));
                        if (cand) { setMsgSearchQuery(""); setMsgSearchResults([]); setChatOpen(cand.id); fetchMessages(cand.id); }
                      }} style={{
                        padding: "6px 8px", borderRadius: 6, marginBottom: 4, cursor: "pointer",
                        backgroundColor: COLORS.surfaceAlt, fontSize: 12,
                      }}>
                        <div style={{ fontWeight: 600, color: COLORS.accent, marginBottom: 2 }}>
                          {r.candidate_name}
                        </div>
                        <div style={{ color: COLORS.textSecondary, lineHeight: 1.3 }}>
                          {r.text?.substring(0, 80)}{r.text?.length > 80 ? "..." : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {msgSearching && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>Searching...</div>}
              </div>
              {/* Chat Messages */}
              <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {chatLoading && (
                  <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>
                    Loading messages...
                  </div>
                )}
                {chatError && (
                  <div style={{ textAlign: "center", padding: 12, color: COLORS.red, fontSize: 12, backgroundColor: COLORS.redLight, borderRadius: 6 }}>
                    {chatError}
                  </div>
                )}
                {!chatLoading && chatMessages.length === 0 && (
                  <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>
                    No messages yet. Start a conversation.
                  </div>
                )}
                {chatMessages.map((m, idx) => {
                  const isRecruiter = (m.sender_role || m.from) === "recruiter";
                  const timestamp = m.created_at
                    ? new Date(m.created_at).toLocaleString()
                    : (m.ts || "");
                  const nextMsg = chatMessages[idx + 1];
                  const isLastInGroup = !nextMsg || ((nextMsg.sender_role || nextMsg.from) !== (m.sender_role || m.from));
                  const hasFile = m.file_url || m.file_name;
                  const isImage = hasFile && IMAGE_TYPES.includes(m.file_type);
                  return (
                    <div key={m.id} style={{
                      alignSelf: isRecruiter ? "flex-end" : "flex-start",
                      maxWidth: "85%",
                    }}>
                      <div style={{
                        padding: "8px 12px", borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                        backgroundColor: isRecruiter ? COLORS.accent : COLORS.surfaceAlt,
                        color: isRecruiter ? "#fff" : COLORS.text,
                        borderBottomRightRadius: isRecruiter ? 4 : 12,
                        borderBottomLeftRadius: isRecruiter ? 12 : 4,
                        opacity: String(m.id).startsWith("temp_") ? 0.7 : 1,
                      }}>
                        {hasFile && isImage && m.file_url && (
                          <a href={m.file_url} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginBottom: m.text ? 6 : 0 }}>
                            <img src={m.file_url} alt={m.file_name || "Image"} style={{
                              maxWidth: 200, maxHeight: 200, borderRadius: 8, display: "block",
                            }} />
                          </a>
                        )}
                        {hasFile && !isImage && (
                          <a href={m.file_url || "#"} target="_blank" rel="noopener noreferrer" style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                            borderRadius: 8, marginBottom: m.text ? 6 : 0, textDecoration: "none",
                            backgroundColor: isRecruiter ? "rgba(255,255,255,0.15)" : COLORS.bg,
                            color: isRecruiter ? "#fff" : COLORS.accent,
                          }}>
                            <span style={{ fontSize: 18 }}>📄</span>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600 }}>{m.file_name || "File"}</div>
                              {m.file_size && <div style={{ fontSize: 10, opacity: 0.7 }}>{formatFileSize(m.file_size)}</div>}
                            </div>
                          </a>
                        )}
                        {m.text && <span>{m.text}</span>}
                      </div>
                      <div style={{
                        fontSize: 10, color: COLORS.textMuted, marginTop: 3, padding: "0 4px",
                        textAlign: isRecruiter ? "right" : "left",
                      }}>
                        {timestamp}
                        {isRecruiter && isLastInGroup && !String(m.id).startsWith("temp_") && (
                          <span style={{ marginLeft: 6, color: m.read_at ? COLORS.accent : COLORS.textMuted }}>
                            {m.read_at ? "✓ Read" : "✓ Sent"}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Typing indicator */}
                {isStudentTyping && (
                  <div style={{ alignSelf: "flex-start", maxWidth: "85%" }}>
                    <div style={{
                      padding: "8px 12px", borderRadius: 12, fontSize: 13,
                      backgroundColor: COLORS.surfaceAlt, color: COLORS.textMuted,
                      borderBottomLeftRadius: 4, display: "flex", alignItems: "center", gap: 4,
                    }}>
                      <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
                        <span style={typingDotStyle(0)} />
                        <span style={typingDotStyle(1)} />
                        <span style={typingDotStyle(2)} />
                      </span>
                      <span style={{ marginLeft: 4 }}>typing...</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              {/* Attachment Preview */}
              {attachFile && (
                <div style={{
                  padding: "8px 12px", borderTop: `1px solid ${COLORS.border}`,
                  display: "flex", alignItems: "center", gap: 8, backgroundColor: COLORS.surfaceAlt,
                }}>
                  {attachPreview ? (
                    <img src={attachPreview} alt="Preview" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                  ) : (
                    <span style={{ fontSize: 24 }}>📄</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachFile.name}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted }}>{formatFileSize(attachFile.size)}</div>
                  </div>
                  <button onClick={clearAttachment} style={{
                    border: "none", background: "none", cursor: "pointer", fontSize: 16,
                    color: COLORS.textMuted, padding: 4,
                  }}>✕</button>
                </div>
              )}
              {/* Chat Input */}
              <div style={{
                padding: 12, borderTop: `1px solid ${COLORS.border}`,
                display: "flex", gap: 8, alignItems: "center",
              }}>
                <input type="file" ref={fileInputRef} onChange={handleFileSelect}
                  accept="image/*,.pdf,.docx,.doc" style={{ display: "none" }} />
                <button onClick={() => fileInputRef.current?.click()} style={{
                  border: "none", background: "none", cursor: "pointer", fontSize: 18,
                  padding: "4px 2px", color: COLORS.textSecondary, flexShrink: 0,
                }}>📎</button>
                <input
                  value={msgDraft} onChange={e => { setMsgDraft(e.target.value); sendTypingSignal(); }}
                  onKeyDown={e => e.key === "Enter" && sendMessage()}
                  placeholder="Message student..."
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                  }}
                />
                <Button small variant="primary" onClick={sendMessage}
                  style={{ opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? "..." : "Send"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Limit Reached Modal */}
      {limitReachedType && (
        <div style={{
          position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000,
        }} onClick={() => setLimitReachedType(null)}>
          <div style={{
            backgroundColor: COLORS.surface, borderRadius: 12, padding: 32,
            maxWidth: 400, width: "90%", textAlign: "center",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              {"\u{1F4C4}"}
            </div>
            <h3 style={{
              fontSize: 18, fontWeight: 700, color: COLORS.text,
              margin: "0 0 8px", letterSpacing: "-0.01em",
            }}>
              CV Request Limit Reached
            </h3>
            <p style={{
              fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.6,
              margin: "0 0 8px",
            }}>
              You've used all 5 free CV requests.
            </p>
            <p style={{
              fontSize: 13, color: COLORS.textMuted, margin: "0 0 20px",
            }}>
              {profile?.tier === "free"
                ? "Upgrade to Pro for 100 CV requests per year."
                : "Contact us for additional access."}
            </p>
            {profile?.tier === "free" ? (
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <Button onClick={() => setLimitReachedType(null)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setLimitReachedType(null);
                  setShowPricing(true);
                }} variant="primary">
                  View Plans
                </Button>
              </div>
            ) : (
              <Button onClick={() => setLimitReachedType(null)} variant="primary">
                OK
              </Button>
            )}
          </div>
        </div>
      )}

      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          currentTier={profile?.tier || "free"}
        />
      )}
      {/* Rejection Dialog */}
      {rejectDialog && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => { setRejectDialog(null); setRejectMessage(""); }}>
          <div style={{
            backgroundColor: COLORS.surface, borderRadius: 12, padding: 28, width: 480, maxWidth: "90vw",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: "0 0 4px" }}>
              Reject {rejectDialog.name}?
            </h3>
            <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: "0 0 16px" }}>
              {EARLY_STAGES.includes(rejectDialog.stage)
                ? "A respectful message will be sent to the student. You can edit it below."
                : `This candidate reached the ${rejectDialog.stage} stage — please write a personal message.`
              }
            </p>
            <textarea
              value={rejectMessage}
              onChange={e => setRejectMessage(e.target.value)}
              placeholder={EARLY_STAGES.includes(rejectDialog.stage) ? "" : "Write a personal message to the student..."}
              rows={5}
              style={{
                width: "100%", padding: 12, borderRadius: 8, border: `1px solid ${COLORS.border}`,
                fontSize: 13, fontFamily: "inherit", resize: "vertical", lineHeight: 1.5,
                color: COLORS.text, backgroundColor: COLORS.bg,
              }}
            />
            {!rejectMessage.trim() && !EARLY_STAGES.includes(rejectDialog.stage) && (
              <p style={{ fontSize: 11, color: COLORS.red, margin: "6px 0 0" }}>
                A personal message is required for candidates at the {rejectDialog.stage} stage.
              </p>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <Button onClick={() => { setRejectDialog(null); setRejectMessage(""); }}>Cancel</Button>
              <Button
                variant="danger"
                onClick={confirmReject}
                style={{ opacity: !rejectMessage.trim() || rejectSending ? 0.5 : 1, pointerEvents: !rejectMessage.trim() || rejectSending ? "none" : "auto" }}
              >
                {rejectSending ? "Sending..." : "Reject & Send Message"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
